import { useCallback, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { Button as ButtonV2, Modal, ModalContent } from '@restworld/ui-ds-base';
import { cn } from '@restworld/utils-common';
import { useSurvey } from '@restworld/data-services';

const FEEDBACK_SURVEY_KEY = 'open-feedback-employer';
const FEEDBACK_MAX_LENGTH = 2000;

type DashboardFeedbackDialogProps = {
  isOpen: boolean;
  onClose: () => void;
};

/**
 * The open-feedback survey, rendered by us and stored in our own backend
 * (`useSurvey`) instead of PostHog, so it works for every logged-in employer,
 * cookie consent or not. Opened on demand from `DashboardFeedbackCard`;
 * repeatable by design (backend dedup `:none`), so no eligibility gating here.
 */
export function DashboardFeedbackDialog({ isOpen, onClose }: DashboardFeedbackDialogProps) {
  const { t } = useTranslation('dashboard-page');
  const { submit, isSubmitting } = useSurvey(FEEDBACK_SURVEY_KEY);

  const [feedback, setFeedback] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleClose = useCallback(() => {
    // Reset so reopening starts a fresh submission, not the previous thank-you.
    setFeedback('');
    setHasSubmitted(false);
    setHasError(false);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    const trimmed = feedback.trim();
    if (trimmed.length === 0 || isSubmitting) return;

    setHasError(false);
    try {
      await submit({ feedback: trimmed });
      setHasSubmitted(true);
    } catch {
      // The answer is still in the textarea: surface the failure and let the
      // employer retry instead of silently swallowing their feedback.
      setHasError(true);
    }
  }, [feedback, isSubmitting, submit]);

  const footerEl = hasSubmitted ? (
    <ButtonV2 variant="primary" fullwidth onClick={handleClose}>
      {t('feedback_dialog.close')}
    </ButtonV2>
  ) : (
    <ButtonV2
      variant="primary"
      fullwidth
      onClick={handleSubmit}
      disabled={feedback.trim().length === 0 || isSubmitting}
    >
      {t('feedback_dialog.submit')}
    </ButtonV2>
  );

  return (
    <Modal open={isOpen} onOpenChange={(v) => !v && handleClose()}>
      <ModalContent
        variant="center"
        className="max-w-[480px]"
        title={hasSubmitted ? t('feedback_dialog.thanks_title') : t('feedback_dialog.question')}
        footer={footerEl}
      >
        <Body>
          {hasSubmitted ? (
            <Description>{t('feedback_dialog.thanks_description')}</Description>
          ) : (
            <>
              <Description>{t('feedback_dialog.description')}</Description>
              {/* Plain styled textarea: ui-ds-base has TextInput but no multi-line primitive. */}
              <FeedbackTextArea
                value={feedback}
                maxLength={FEEDBACK_MAX_LENGTH}
                placeholder={t('feedback_dialog.placeholder') ?? ''}
                onChange={(event) => setFeedback(event.target.value)}
                rows={5}
                autoFocus
              />
              {hasError && <ErrorText role="alert">{t('feedback_dialog.error')}</ErrorText>}
            </>
          )}
        </Body>
      </ModalContent>
    </Modal>
  );
}

export default DashboardFeedbackDialog;

const Body = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-3 w-full pt-2 pb-6', className)} {...props} />
);
const Description = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p className={cn('text-sm text-neutral-600 m-0', className)} {...props} />
);
const FeedbackTextArea = ({ className, ...props }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea
    className={cn(
      'w-full resize-none rounded-lg border border-neutral-40 bg-white p-3 text-base text-neutral-700 focus:outline-none focus:border-purple-500',
      className
    )}
    {...props}
  />
);
const ErrorText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p className={cn('text-sm text-red-500 m-0', className)} {...props} />
);
